'use client';
import { timeout } from '@/utils/utils/delay';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

interface CarouselProperty {
  children: React.ReactNode;
  speed?: number;
  height?: number;
  style?: string;
  delay?: number;
}

export const FilpCarousel: React.FC<CarouselProperty> = ({ children, speed = 5000, style, delay }) => {
  // 取得目前輪播物件個數
  const total = React.Children.count(children) || 0;
  const timerRef = useRef<number | null>();
  const [current, setCurrent] = useState(0);
  const [animationStep, setAnimationStep] = useState(0);
  const carouselTotal = total + 2;
  const firstChild = children && Array.isArray(children) ? children[0] : null;
  const lastChild = children && Array.isArray(children) ? children[children.length - 1] : null;

  const carouselItemStyle = {
    height: `${Math.round(10000 / carouselTotal) / 100}%`
  };

  const transitionStyle = {
    transition: `transform ${animationStep}s`,
    height: `${carouselTotal * 100}%`,
    width: '100%',
    transform: `translateY(${(-100 / carouselTotal) * (current + 1)}%)`
  };

  const renderCarouselItem = (child: React.ReactNode, index = 0) => {
    if (!child) return null;

    return (
      <div style={carouselItemStyle} key={`carousel-item-${index}`}>
        {child}
      </div>
    );
  };

  const stopCarousel = () => {
    timerRef.current && clearInterval(timerRef.current);
  };

  const startCarousel = () => {
    stopCarousel();

    timerRef.current = window.setInterval(() => {
      if (current % (total + 1) !== total && current >= 0) {
        setAnimationStep(1);
        // 當座標在邊界時，會取消動畫過場的秒數，當目前座標不是邊界時，要恢復動畫過場秒數，不然無法正確播放動畫
        setCurrent(prevState => {
          const current = prevState + 1;

          return current % (total + 1);
        });
      }
    }, speed);
  };

  const handleTransitionEnd = () => {
    // 當輪播最後一個時，取消過場動畫，並將座標位置重設為 0
    if (current % (total + 1) === total) {
      setAnimationStep(0);
      setCurrent(0);
      // 當輪播第一個時，取消過場動畫，並將座標位置設為最後一個
    } else if (current < 0) {
      setAnimationStep(0);
      setCurrent(total - 1);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (delay) await timeout(delay);
      startCarousel();
    };

    init();

    return () => {
      stopCarousel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classNames('overflow-hidden', style)}>
      <div
        className={classNames('relative overflow-hidden')}
        onTransitionEnd={handleTransitionEnd}
        style={transitionStyle}
      >
        {renderCarouselItem(lastChild, 0)}
        {children && Array.isArray(children) && children.map((item, index) => renderCarouselItem(item, index + 1))}
        {renderCarouselItem(firstChild, carouselTotal)}
      </div>
    </div>
  );
};

export default FilpCarousel;
